import React from "react";
import dynamic from "next/dynamic";
import { useIntercom } from "react-use-intercom";

import { Link } from "components/common/Link";
import { useShowNeedHelpBanner } from "components/dashboard/Checklist/NeedHelpBanner";

import { Briefcase, ChevronDown, Headset } from "@puzzle/icons";

import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import { useAppRouter } from "lib/useAppRouter";

import { Route } from "lib/routes";
import links from "lib/links";

import { Menu } from "@puzzle/ui";
import { Text, Button, Skeleton } from "@puzzle/ve";
import { S } from "@puzzle/theme";
import { zIndex } from "@puzzle/utils";

import { ProfileMenu } from "components/layout/private/SidebarV2/ProfileMenu";
import { useLazyLoading } from "domains/app/LazyLoadingProvider/LazyLoadingProvider";

const DynamicOnboardingLaunchpadButton = dynamic(() =>
  import("domains/app/components/OnboardingLaunchpadButton/OnboardingLaunchpadButton").then(
    (mod) => mod.OnboardingLaunchpadButton
  )
);

const HIRE_ACCOUNTANT_BUTTON_WIDTH = "194px";
const HIRE_ACCOUNTANT_BUTTON_HEIGHT = "36px";
const LAUNCHPAD_BUTTON_WIDTH = "144px";
const LAUNCHPAD_BUTTON_HEIGHT = "36px";

type HelpMenuProps = {
  showChatWithUs: boolean;
  showAskAnAccountant: boolean;
  showShareFeedback: boolean;
};

const HelpMenu = ({ showChatWithUs, showAskAnAccountant, showShareFeedback }: HelpMenuProps) => {
  const { show: showIntercom } = useIntercom();
  const { goToPath } = useAppRouter();

  return (
    <Menu
      css={{ zIndex: zIndex("menu") }}
      trigger={
        <Button
          variant="minimal"
          css={{ display: "flex", flexDirection: "row", alignItems: "center", gap: S.$1h }}
          prefixElement={<Headset size={20} style={{ marginRight: S.$0h }} />}
          suffixElement={<ChevronDown size={16} />}
        >
          Get help
        </Button>
      }
    >
      {showChatWithUs && (
        <Menu.Item
          onClick={() => {
            showIntercom();
          }}
        >
          <Text variant="bodyS" color="neutral-subdue">
            Chat with us
          </Text>
        </Menu.Item>
      )}
      {showAskAnAccountant && (
        <Menu.Item
          onClick={() => {
            goToPath(Route.askAccountant);
          }}
        >
          <Text variant="bodyS" color="neutral-subdue">
            Ask an accountant
          </Text>
        </Menu.Item>
      )}
      <Menu.Item>
        <Link href={links.helpCenter} external target="_blank">
          <Text variant="bodyS" color="neutral-subdue">
            Help center
          </Text>
        </Link>
      </Menu.Item>
      <Menu.Item>
        <Link href={links.blog} external target="_blank">
          <Text variant="bodyS" color="neutral-subdue">
            Blog
          </Text>
        </Link>
      </Menu.Item>
      <Menu.Item>
        <Link href={links.foundersGuide} external target="_blank">
          <Text variant="bodyS" color="neutral-subdue">
            Founder's guide
          </Text>
        </Link>
      </Menu.Item>
      {showShareFeedback && (
        <Menu.Item
          onClick={() => {
            showIntercom();
          }}
        >
          <Text variant="bodyS" color="neutral-subdue">
            Share feedback
          </Text>
        </Menu.Item>
      )}
    </Menu>
  );
};

const HireAnAccountant = () => {
  // Note: This button is not a link, it's a button that opens the Intercom widget
  return (
    <Button
      id="header-hire-an-accountant"
      prefixElement={<Briefcase size={20} css={{ marginRight: S.$0h }} />}
      variant="minimal"
    >
      Hire an accountant
    </Button>
  );
};

export const HeaderButtons = ({ hangRight = false }: { hangRight?: boolean }) => {
  const { showHireAnAccountant, showChatWithUs, showAskAnAccountant, showShareFeedback, loading } =
    useShowNeedHelpBanner();

  const { hasVisuallyLoaded } = useLazyLoading();
  const shouldShowOnboardingLaunchpad = hasVisuallyLoaded;

  const isPartialMinimizedEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.SidebarPartialMinimized
  );

  // hire an accountant button will be disabled when the firm select feature is live
  const hireAnAccountantEnabled = !isPosthogFeatureFlagEnabled(FeatureFlag.FirmSelect);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginLeft: hangRight ? "auto" : S.$2,
        gap: S.$2,
      }}
    >
      {shouldShowOnboardingLaunchpad ? (
        <DynamicOnboardingLaunchpadButton />
      ) : (
        <Skeleton
          width={LAUNCHPAD_BUTTON_WIDTH}
          height={LAUNCHPAD_BUTTON_HEIGHT}
          variant="rounded"
        />
      )}
      {hireAnAccountantEnabled && (
        <>
          {loading ? (
            <Skeleton
              width={HIRE_ACCOUNTANT_BUTTON_WIDTH}
              height={HIRE_ACCOUNTANT_BUTTON_HEIGHT}
              variant="rounded"
            />
          ) : (
            showHireAnAccountant && <HireAnAccountant />
          )}
        </>
      )}
      <HelpMenu
        showChatWithUs={showChatWithUs}
        showAskAnAccountant={showAskAnAccountant}
        showShareFeedback={showShareFeedback}
      />
      {isPartialMinimizedEnabled && <ProfileMenu minimized={false} />}
    </div>
  );
};
